// Debriefs (D20, DC-170; F-176 every seat that takes part): the viewer's debrief cycles — the requisition, the debrief stage on its pill, the
// viewer's place on the attendance roll and the scorecards still due — each row opening the viewer's own debrief view.
// The server's attendance word is never drawn raw: Required reads as his *Required*, a missed debrief as DS §2.4's
// *Missing attendee*, and attendance as the check alone (G §22 leaves the present word open).

import { debriefListMine, type CycleRow } from "@/api/tools";
import { useRead } from "@/api/useTool";
import {
  COMMON_LABELS, Card, Count, DEBRIEF_ROOM_LABELS, DEBRIEF_STATUS, EmptyState, Icon, McRoot, NAV_ITEM_LABELS, PageHeader, Pill, SLA_LABELS,
  SURFACE_LABELS, Stack, Table, Td, TextLink, Th, Tr, VisuallyHidden, type Tone,
} from "@/mc";
import { SCORE_LABELS } from "./labels";

type DebriefStage = keyof typeof DEBRIEF_STATUS;

/** The server's cycle status to G §11's chain; a status the chain does not name draws no pill. */
const STAGE_OF: Readonly<Record<string, DebriefStage>> = {
  not_ready: "notReady", scheduled: "scheduled", roll_call: "rollCall", reveal: "reveal", revealed: "reveal",
  discussion: "discussion", alignment: "alignment", outcome_recorded: "outcomeRecorded", decided: "outcomeRecorded",
  closed: "outcomeRecorded", blocked: "blocked",
};

const STAGE_TONE: Readonly<Record<DebriefStage, Tone>> = {
  notReady: "neutral", scheduled: "info", rollCall: "info", reveal: "info", discussion: "info", alignment: "info",
  outcomeRecorded: "ok", blocked: "red",
};

export function stageOf(status: string): DebriefStage | null {
  return STAGE_OF[status] ?? null;
}

export type RollMark = "required" | "missing" | "attended" | null;

/** The viewer's place on the roll from the server's attendance key (never its label). */
export function rollOf(row: Pick<CycleRow, "my_attendance">): RollMark {
  switch (row.my_attendance) {
    case "required": return "required";
    case "not_attended": case "absent": case "missing": return "missing";
    case "attended": case "present": return "attended";
    default: return null;
  }
}

export function RollCell({ mark }: { mark: RollMark }) {
  if (mark === "required") return <Pill tone="info">{COMMON_LABELS.required}</Pill>;
  if (mark === "missing") return <Pill tone="warn">{DEBRIEF_ROOM_LABELS.missingAttendee}</Pill>;
  // G §22 leaves the present word open: the check carries it, with the roll's name for the reader, until his word lands.
  if (mark === "attended") return <span data-roll="attended"><Icon name="check" /><VisuallyHidden>{SURFACE_LABELS.attendanceRoll}</VisuallyHidden></span>;
  return null;
}

/** DC-170's bands: the cycles still ahead (scorecards due first), then the ones whose outcome is recorded. */
export function bandCycles(rows: readonly CycleRow[]): { open: CycleRow[]; done: CycleRow[] } {
  const done = rows.filter((row) => stageOf(row.status) === "outcomeRecorded");
  const open = rows.filter((row) => stageOf(row.status) !== "outcomeRecorded");
  open.sort((a, b) => b.my_scorecards_due - a.my_scorecards_due || (a.start ?? "~").localeCompare(b.start ?? "~"));
  return { open, done };
}

/** F-176: where a row opens. An interviewer on the roll opens their own debrief view; the facilitating Recruiter, the
 * Hiring Manager, the HR Business Partner, a room delegate and the Admin open the debrief room itself. */
const ROOM_ROLES: ReadonlySet<string> = new Set(["facilitator", "hm", "hrbp"]);

export function debriefHref(row: Pick<CycleRow, "cycle_id" | "requisition_id" | "my_role" | "my_attendance">): string {
  return ROOM_ROLES.has(row.my_role) || row.my_attendance === null
    ? `#requisitions/${row.requisition_id}/debriefs/${row.cycle_id}`
    : `#interviews/debriefs/${row.cycle_id}`;
}

export function DebriefRow({ row, index }: { row: CycleRow; index: number }) {
  const stage = stageOf(row.status);
  return (
    <Tr >
      <Td label={SCORE_LABELS.requisition} sub={row.requisition_id}>
        <TextLink href={debriefHref(row)} hs={index === 0 ? "score.mydebrief" : undefined}>{row.requisition_title}</TextLink>
      </Td>
      <Td label={SCORE_LABELS.stage}>{stage ? <Pill tone={STAGE_TONE[stage]}>{DEBRIEF_STATUS[stage]}</Pill> : null}</Td>
      <Td label={SURFACE_LABELS.attendanceRoll}><RollCell mark={rollOf(row)} /></Td>
      <Td label={SLA_LABELS.scorecardDue} align="r">{row.my_scorecards_due > 0 ? <Count n={row.my_scorecards_due} label={SLA_LABELS.scorecardDue} /> : null}</Td>
    </Tr>
  );
}

export function DebriefsTable({ rows }: { rows: readonly CycleRow[] }) {
  const { open, done } = bandCycles(rows);
  const band = (key: string, title: string | null, list: CycleRow[], offset: number) => list.length === 0 ? null : (
    <tbody key={key}>
      {title ? <Tr><Th row>{title}</Th><Td colSpan={3}>{null}</Td></Tr> : null}
      {list.map((row, i) => <DebriefRow key={row.cycle_id} row={row} index={offset + i} />)}
    </tbody>
  );
  return (
    <Table
      label={NAV_ITEM_LABELS.debriefs}
      head={<Tr><Th>{SCORE_LABELS.requisition}</Th><Th>{SCORE_LABELS.stage}</Th><Th>{SURFACE_LABELS.attendanceRoll}</Th><Th align="r">{SLA_LABELS.scorecardDue}</Th></Tr>}
      bodies={[band("open", null, open, 0), band("done", DEBRIEF_STATUS.outcomeRecorded, done, open.length)]}
      cols={4}
    />
  );
}

function DebriefsBody() {
  const list = useRead(["debriefs", "mine"], () => debriefListMine({}));
  const rows = list.data?.items ?? [];
  return (
    <Stack gap={16}>
      <PageHeader title={NAV_ITEM_LABELS.debriefs} />
      <Card loading={list.isLoading ? true : undefined}>
        {rows.length === 0 && !list.isLoading ? <EmptyState icon="calendar">{NAV_ITEM_LABELS.debriefs}</EmptyState> : <DebriefsTable rows={rows} />}
      </Card>
    </Stack>
  );
}

export function Debriefs() {
  return <McRoot><DebriefsBody /></McRoot>;
}
